import { Text } from '@amo/core/components/forms';
import { getCity, getCityBySearch, postLogPostalCode } from '@amo/core/utils/requestsApi';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import { Field } from 'redux-form';

export const PostalCodeCity = (props) => {
    const {
        id,
        nameVille,
        changeValue,
        nameCodePostal,
        defaultValue,
        validate,
        placeholder,
        label,
        ariaLabel,
        ariaLabelledBy,
        required,
        disabled,
        token,
        canal,
        onChange
    } = props;

    const [city, setCity] = useState([]);
    const [selected, setSelected] = useState(false);
    const [labelSelected, setLabelSelected] = useState(defaultValue);
    const [loading, setLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    useEffect(() => {
        if (defaultValue){
            setLabelSelected(defaultValue)
        }
    }, [defaultValue])

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [city]);

    const formatObj = (codePostal, villes) => {
        return villes.map((ville) => ({ ville, codePostal }));
    };

    const fetchCityByPostalCode = async (value) => {
        let cities = await getCity(value, setLoading);
        setCity(formatObj(value, cities));
    };

    const fetchCityBySearch = async (value) => {
        let cities = await getCityBySearch(value, setLoading);
        setCity(cities);
    };

    const logPostalCode = async (value) => {
        await postLogPostalCode(canal, token, value);
    };

    const searchCity = (value) => {
        changeValue(nameVille, '');
        changeValue(nameCodePostal, '');
        setSelected(false);
        setLabelSelected(value);
        onChange && onChange(value)

        if (_.parseInt(value)) {
            value.length === 5 && fetchCityByPostalCode(value);
        } else {
            value.length >= 3 && fetchCityBySearch(value);
        }

        if (_.startsWith(value, '97') && value.length === 5 && id === 'lieuStationnement') {
            logPostalCode(value);
        }
    };

    const setCityAndPostalCode = (clickCity) => {
        changeValue(nameVille, clickCity.ville);
        changeValue(nameCodePostal, clickCity.codePostal);
        changeValue(
            `postalCodeCity${id ? `-${id}` : ''}`,
            `${clickCity.codePostal} ${_.startCase(_.toLower(clickCity.ville))}`,
        );
        setSelected(true);
        setLabelSelected(`${clickCity.codePostal} ${_.startCase(_.toLower(clickCity.ville))}`);
    };

    const listboxId = `postalCodeCity-listbox${id ? `-${id}` : ''}`;
    const formatId = `postalCodeCity-format${id ? `-${id}` : ''}`;
    const isExpanded = !selected && city.length > 0;
    const activeOptionId = highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined;

    const handleKeyDown = (e) => {
        if (!isExpanded) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % city.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((i) => (i <= 0 ? city.length - 1 : i - 1));
                break;
            case 'Home':
                e.preventDefault();
                setHighlightedIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setHighlightedIndex(city.length - 1);
                break;
            case 'Enter':
                if (highlightedIndex >= 0) {
                    e.preventDefault();
                    setCityAndPostalCode(city[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setCity([]);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    return (
        <>
            {label && (
                <label htmlFor={`text-postalCodeCity${id ? `-${id}` : ''}`} className={'label-text mb-1'}>
                    {label}
                    {required ? <><sup className={'text-danger'} aria-hidden="true">*</sup><span className="visually-hidden"> (obligatoire)</span></> : ''}
                </label>
            )}
            <Field
                name={`postalCodeCity${id ? `-${id}` : ''}`}
                component={Text}
                typeFormat={'postal'}
                input={{
                    name: `postalCodeCity${id ? `-${id}` : ''}`,
                    valueInput: labelSelected,
                    onChange: (e) => searchCity(e.target.value),
                }}
                validate={validate}
                disabled={disabled}
                placeholder={placeholder}
                required={required}
                describedBy={placeholder ? formatId : undefined}
                role="combobox"
                aria-labelledby={ariaLabelledBy}
                aria-label={label || ariaLabelledBy ? undefined : ariaLabel}
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? listboxId : undefined}
                aria-autocomplete="list"
                aria-activedescendant={activeOptionId}
                onKeyDown={handleKeyDown}
            />

            {placeholder && (
                <small id={formatId} className="visually-hidden">
                    {placeholder}
                </small>
            )}

            {loading && <span className="spinner-border spinner-border-sm" role="status" aria-label="Chargement en cours" />}

            {isExpanded && (
                <div className="input-text-content">
                    <div className="list-group" role="listbox" id={listboxId} aria-labelledby={ariaLabelledBy} aria-label={ariaLabelledBy ? undefined : (label || ariaLabel || 'Résultats ville / code postal')}>
                        {city.map((c, index) => (
                            <li
                                id={`${listboxId}-option-${index}`}
                                className={`text-start cursor-pointer list-group-item list-group-item-action ${highlightedIndex === index ? 'active' : ''}`}
                                key={index}
                                role="option"
                                aria-selected={highlightedIndex === index}
                                value={c.ville}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setCityAndPostalCode(c);
                                }}
                            >
                                {c.codePostal} {_.startCase(_.toLower(c.ville))}
                            </li>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

PostalCodeCity.propTypes = {
    id: PropTypes.string,
    nameVille: PropTypes.string.isRequired,
    changeValue: PropTypes.func.isRequired,
    nameCodePostal: PropTypes.string.isRequired,
    defaultValue: PropTypes.string,
    validate: PropTypes.func,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    ariaLabel: PropTypes.string,
    ariaLabelledBy: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    token: PropTypes.string,
    session_id: PropTypes.string,
    canal: PropTypes.string,
};
